import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';

interface CompanyStudent {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  createdAt: string;
}

interface CompanyResponse {
  id: string;
  name: string;
  slug: string;
}

const CompanyStudents: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [company, setCompany] = useState<CompanyResponse | null>(null);
  const [students, setStudents] = useState<CompanyStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const token = localStorage.getItem('admin_token');
  const apiBaseUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const loadCompanyStudents = async () => {
      if (!companyId) {
        return;
      }

      setLoading(true);
      setError('');
      try {
        const response = await axios.get(`${apiBaseUrl}/companies/${companyId}/students`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCompany(response.data?.company || null);
        setStudents(response.data?.students || []);
      } catch (err: any) {
        setError(err?.response?.data?.error || "Impossible de charger les apprenants de l'entreprise");
      } finally {
        setLoading(false);
      }
    };

    void loadCompanyStudents();
  }, [apiBaseUrl, companyId, token]);

  const columns = useMemo<GridColDef<CompanyStudent>[]>(
    () => [
      {
        field: 'lastName',
        headerName: 'Nom',
        flex: 1,
        minWidth: 140,
      },
      {
        field: 'firstName',
        headerName: 'Prénom',
        flex: 1,
        minWidth: 140,
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1.5,
        minWidth: 200,
        valueGetter: (value) => value || 'Sans email',
      },
      {
        field: 'createdAt',
        headerName: 'Créé le',
        flex: 1,
        minWidth: 130,
        valueFormatter: (value) =>
          value ? new Date(value as string).toLocaleDateString('fr-FR') : '—',
      },
    ],
    []
  );

  return (
    <Container maxWidth="lg">
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {company ? `Entreprise : ${company.name}` : 'Entreprise'}
          </Typography>
          <Typography color="text.secondary">
            Liste des apprenants assignés à cette entreprise
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate('/companies')}>
          Retour aux entreprises
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box mb={2}>
        <Card sx={{ minWidth: 150, display: 'inline-block' }}>
          <CardContent sx={{ textAlign: 'center', py: 2, '&:last-child': { pb: 2 } }}>
            <Typography variant="h5" fontWeight="bold">
              {students.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Apprenant{students.length !== 1 ? 's' : ''}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Card>
        <Box sx={{ height: 560, width: '100%' }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={students}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 10 },
                },
              }}
              pageSizeOptions={[5, 10, 25, 50]}
              disableRowSelectionOnClick
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 300 },
                },
              }}
              localeText={{
                noRowsLabel: 'Aucun apprenant assigné pour le moment.',
              }}
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #f0f0f0',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#fafafa',
                  borderBottom: '2px solid #e0e0e0',
                },
              }}
            />
          )}
        </Box>
      </Card>
    </Container>
  );
};

export default CompanyStudents;
